import { Link } from "react-router-dom";

export default function About() {
  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-sky-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Built by a teacher who understands the assessment problem
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            ExamLock was created to help teachers save time creating assessments and make online testing more transparent, fair, and manageable.
          </p>
        </div>
      </div>

      {/* Founder Story */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">The Story</h2>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-200">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ExamLock was created by Mohamed Abdillahi, a science teacher and grade leader in Qatar, after seeing how much time teachers spend preparing assessments and how difficult it can be to monitor online exams clearly.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                The goal is simple: help teachers create assessments faster, monitor student activity more clearly, and keep the process fair for students.
              </p>
            </div>

            {/* Founder Card */}
            <div className="flex justify-center">
              <div className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md border border-gray-200">
                <div className="text-6xl mb-4">👨‍🏫</div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Mohamed Abdillahi</h3>
                <p className="text-blue-600 font-semibold mb-4">Founder</p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Science teacher and grade leader in Qatar. Built ExamLock from classroom experience to help teachers save time creating assessments and make online testing more transparent and manageable.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Classroom Teaching</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Assessment Design</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Education Technology</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Values */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 text-center">Mission</h2>
            <p className="text-xl text-gray-700 text-center mb-16 max-w-3xl mx-auto">
              To make secure online assessment easier for teachers and more transparent for students.
            </p>

            <h2 className="text-3xl font-bold mb-12 text-gray-900 text-center">Values</h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Value 1 */}
              <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
                <div className="text-4xl mb-4">👩‍🏫</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Teacher-first</h3>
                <p className="text-gray-600 leading-relaxed">
                  Built around real teacher workflows, not complex technical systems.
                </p>
              </div>

              {/* Value 2 */}
              <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
                <div className="text-4xl mb-4">✅</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Trust and integrity</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clear monitoring, fair logs, and transparent assessment rules.
                </p>
              </div>

              {/* Value 3 */}
              <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
                <div className="text-4xl mb-4">⚡</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Simple by design</h3>
                <p className="text-gray-600 leading-relaxed">
                  Fast setup, browser-based access, and clear student instructions.
                </p>
              </div>

              {/* Value 4 */}
              <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
                <div className="text-4xl mb-4">🌍</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Qatar and GCC focus</h3>
                <p className="text-gray-600 leading-relaxed">
                  Designed for schools using IB, IGCSE, NGSS, British curriculum, and bilingual/EAL classrooms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-gray-900 text-center">Timeline</h2>

            <div className="space-y-8">
              {/* Timeline Item 1 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="inline-block px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-semibold">2024</span>
                </div>
                <div className="flex-1 bg-white p-6 rounded-xl shadow-md border border-gray-200">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">The Problem</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Idea developed from classroom assessment challenges: hours spent creating exams and difficulty monitoring online tests fairly.
                  </p>
                </div>
              </div>

              {/* Timeline Item 2 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="inline-block px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-semibold">2025</span>
                </div>
                <div className="flex-1 bg-white p-6 rounded-xl shadow-md border border-gray-200">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Private Beta</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Private beta for early teachers in Qatar/GCC. Working with teachers in Qatar schools and GCC international schools to build assessment tools that meet real classroom needs.
                  </p>
                </div>
              </div>

              {/* Timeline Item 3 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="inline-block px-3 py-1 bg-green-600 text-white rounded-full text-sm font-semibold">Now</span>
                </div>
                <div className="flex-1 bg-white p-6 rounded-xl shadow-md border border-gray-200">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Refining the Product</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Refining secure assessment workflows through real classroom feedback from Qatar and GCC teachers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Join the private beta
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Help shape ExamLock while using it with your classes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/teacher-register"
              className="px-8 py-4 bg-white hover:bg-gray-100 text-blue-600 rounded-lg font-semibold text-lg shadow-lg transition-all"
            >
              Try ExamLock Free
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-blue-700 hover:bg-blue-800 text-white rounded-lg font-semibold text-lg border-2 border-white border-opacity-30 transition-all"
            >
              Contact About a School Pilot
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
